import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Container, Row, Col, Form, Button, ButtonGroup,
  OverlayTrigger, Tooltip, Modal, Offcanvas, Accordion, Badge, Spinner
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getSalesReportByPracticeNumbers } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { unparse } from "papaparse";
import "./CreateInvoice.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function TransactionList() {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ sumExcl: 0, sumVat: 0, sumTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  const [page, setPage] = useState(1);
  const limit = 20;

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  const statusOptions = ["Posted", "Partially Paid", "Paid", "Cancelled", "Written Off"];

  // Init dates once
  useEffect(() => {
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(start));
    setEndDate(iso(today));
    setTmpStartDate(iso(start));
    setTmpEndDate(iso(today));
  }, []);

  // Fetch practices
  useEffect(() => {
    async function fetchPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error fetching practices:", e);
        setPracticeList([]);
      }
    }
    fetchPractices();
  }, []);

  // Backend-driven fetch — fires on every filter/sort/page change
  useEffect(() => {
    if (!startDate || !endDate || practiceList.length === 0) return;

    async function fetchData() {
      try {
        setLoading(true);
        // Map selected practice names → practice numbers (all if none selected)
        const practiceNums =
          selectedPractices.length > 0
            ? practiceList
                .filter((p) => selectedPractices.includes(p.practiceName))
                .map((p) => p.practiceNumber)
            : practiceList.map((p) => p.practiceNumber);

        const result = await getSalesReportByPracticeNumbers({
          practiceNumbers: practiceNums,
          startDate,
          endDate,
          statuses: selectedStatuses,
          sortKey: sortConfig.key,
          sortDir: sortConfig.direction,
          page,
          limit,
        });
        setInvoices(result.data || []);
        setTotalCount(result.total || 0);
        setTotals(result.totals || { sumExcl: 0, sumVat: 0, sumTotal: 0 });
        setExpandedRows(new Set());
      } catch (e) {
        console.error("Error fetching invoices:", e);
        setInvoices([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [practiceList, selectedStatuses, selectedPractices, startDate, endDate, sortConfig, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, startDate, endDate]);

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  // Derive display rows from paged backend data
  const displayRows = useMemo(() => invoices.map((inv) => ({ type: "invoice", inv })), [invoices]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    setPage(1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const practiceNums =
        selectedPractices.length > 0
          ? practiceList
              .filter((p) => selectedPractices.includes(p.practiceName))
              .map((p) => p.practiceNumber)
          : practiceList.map((p) => p.practiceNumber);

      const result = await getSalesReportByPracticeNumbers({
        practiceNumbers: practiceNums,
        startDate,
        endDate,
        statuses: selectedStatuses,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
        page: 1,
        limit: 99999,
      });

      const allInvoices = result.data || [];
      const exportData = allInvoices.reduce((acc, inv) => {
        const invDate = inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : "";
        const practiceName = inv.practice?.practiceName || "";

        // One row per invoice line
        (inv.lines || []).forEach((line) => {
          const sp = line.serviceProvider
            ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
            : inv.practitioner?.firstName
              ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
              : "";
          acc.push({
            InvoiceDate: invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: line.tariffCode || line.nappiCode || "",
            Description: line.description || "",
            Quantity: line.quantity ?? 1,
            Rate: line.rate || 0,
            ExclAmount: inv.exclAmount || 0,
            VatAmount: inv.vatAmount || 0,
            CN_WO_Number: "",
            CN_WO_Type: "",
            CN_WO_Amount: "",
            ServiceProvider: sp,
            PracticeName: practiceName,
          });
        });

        // One row per credit note
        (inv.receipts || []).filter((r) => r.creditNoteNumber).forEach((r) => {
          acc.push({
            InvoiceDate: r.creditNoteDate ? new Date(r.creditNoteDate).toISOString().slice(0, 10) : invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: "",
            Description: `Credit Note ${r.creditNoteNumber || ""}`,
            Quantity: "",
            Rate: -(r.creditNoteAmount || 0),
            ExclAmount: "",
            VatAmount: "",
            ServiceProvider: "",
            PracticeName: practiceName,
          });
        });

        // One row per write-off
        (inv.receipts || []).filter((r) => r.writeOffNumber).forEach((r) => {
          acc.push({
            InvoiceDate: r.writeOffDate ? new Date(r.writeOffDate).toISOString().slice(0, 10) : invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: "",
            Description: `Write Off ${r.writeOffNumber || ""}`,
            Quantity: "",
            Rate: -(r.writeOffAmount || 0),
            ExclAmount: "",
            VatAmount: "",
            ServiceProvider: "",
            PracticeName: practiceName,
          });
        });

        return acc;
      }, []);

      const csv = unparse(exportData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "transaction-report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExportLoading(false);
    }
  };

  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedStatuses([]);
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);
    setTmpPractices([]);
    setTmpStatuses([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  return (
    <Container fluid className="pt-2">
      {/* Top bar */}
      <Row className="mb-3 align-items-center">
        {/* Totals Table */}
        <Col md={5}>
          <span className="text-primary fw-semibold">Total Transactions:</span>
          <Table bordered size="sm" className="mb-0 text-center align-middle">
            <thead className="table-primary">
              <tr>
                <th style={{ width: "33%" }} className="fw-semibold text-primary">Excluding VAT</th>
                <th style={{ width: "33%" }} className="fw-semibold text-primary">Including VAT</th>
                <th style={{ width: "34%" }} className="fw-semibold text-primary">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{currency.format(totals.sumExcl)}</td>
                <td>{currency.format(totals.sumVat)}</td>
                <td><strong>{currency.format(totals.sumTotal)}</strong></td>
              </tr>
            </tbody>
          </Table>
        </Col>

        {/* Actions */}
        <Col md={7} className="d-flex gap-2 flex-wrap justify-content-end">
          <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
            <i className="fa-solid fa-filter me-2" />
            Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedStatuses.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>

          {/* Pagination */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">Page {page} of {totalPages}</div>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>

          {/* Export + Info */}
          <ButtonGroup className="ms-1 gap-1">
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Export to CSV</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleExportCSV} disabled={exportLoading}>
                {exportLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fa-solid fa-file-export" />
                )}
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Info</Tooltip>}>
              <Button variant="outline-secondary" onClick={() => setShowInfo(true)}>
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Transactions...</p>
        </div>
      ) : displayRows.length === 0 ? (
        <p>No transactions found. The default filter is for the last 7 days.</p>
      ) : (
        <Table bordered hover responsive className="fixed-table">
          <thead className="table-primary text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "4%" }}></th>
              <th
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
                onClick={() => handleSort("invoiceDate")}
              >
                Date {renderSortIcon("invoiceDate")}
              </th>
              <th style={{ width: "8%" }} className="text-primary fw-semibold">Inv Number</th>
              <th style={{ width: "20%" }} className="text-primary fw-semibold">Practice Name</th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("accountName")}
                className="text-primary fw-semibold"
              >
                Account Name {renderSortIcon("accountName")}
              </th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("status")}
                className="text-primary fw-semibold"
              >
                Status {renderSortIcon("status")}
              </th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*Excl Amount</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*VAT Amount</th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("totalAmount")}
                className="text-primary fw-semibold"
              >
                Total {renderSortIcon("totalAmount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              if (row.type === "invoice") {
                const inv = row.inv;
                const isExpanded = expandedRows.has(inv._id);
                const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                const adjustedTotal = (inv.totalAmount || 0) - totalCN - totalWO;
                return (
                  <React.Fragment key={`inv-${inv._id}`}>
                    <tr
                      style={{
                        cursor: "pointer",
                        "--bs-table-bg": isExpanded ? "#CFE3FF4F" : "",
                        "--bs-table-color": isExpanded ? "#0f3490" : "",
                        ...(isExpanded
                          ? { color: "#cfe2ff", borderTop: "1px solid #0f3490", fontWeight: 500 }
                          : {}),
                      }}
                      onClick={() => toggleRow(inv._id)}
                    >
                      <td className="text-center align-middle">
                        <i className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`} />
                      </td>
                      <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            inv.status === "Pending"
                              ? `/invoices/${inv._id}/${inv.accountId}`
                              : `/invoices/posted/${inv._id}`
                          );
                        }}
                      >
                        {inv.invoiceNumber}
                      </td>
                      <td>{inv.practice?.practiceName}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/accounts/${inv.accountId}`);
                        }}
                      >
                        {inv.accountName}
                      </td>
                      <td>{inv.status}</td>
                      <td className="text-end">{inv.exclAmount ? currency.format(inv.exclAmount) : "-"}</td>
                      <td className="text-end">{inv.vatAmount ? currency.format(inv.vatAmount) : "-"}</td>
                      <td className="text-end fw-bold">{currency.format(adjustedTotal)}</td>
                    </tr>
                    {isExpanded && (
                      <tr key={`inv-${inv._id}-lines`}>
                        <td colSpan={2}></td>
                        <td colSpan={7} style={{ padding: "0.25rem 0.5rem" }}>
                          {!inv.lines || inv.lines.length === 0 ? (
                            <span className="text-muted small">No line items</span>
                          ) : (
                            <table
                              className="table table-sm table-borderless mb-0"
                              style={{ fontSize: "0.85rem", tableLayout: "fixed", width: "100%" }}
                            >
                              <colgroup>
                                <col style={{ width: "11%" }} />
                                <col style={{ width: "40%" }} />
                                <col style={{ width: "19%" }} />
                                <col style={{ width: "5%" }} />
                                <col style={{ width: "10%" }} />
                              </colgroup>
                              <tbody>
                                {/* Invoice lines */}
                                {inv.lines.map((line, li) => (
                                  <tr key={li}>
                                    <td className="text-primary">{line.tariffCode || line.nappiCode || "-"}</td>
                                    <td className="text-primary">{line.description || "-"}</td>
                                    <td className="text-primary">
                                      {line.serviceProvider
                                        ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                        : inv.practitioner?.firstName
                                          ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                          : "-"}
                                    </td>
                                    <td className="text-center text-primary">{line.quantity ?? 1}</td>
                                    <td className="text-end text-primary">{currency.format(line.rate || 0)}</td>
                                  </tr>
                                ))}
                                {/* Credit note lines */}
                                {creditNotes.map((r, ci) => (
                                  <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                  </tr>
                                ))}
                                {/* Write-off lines */}
                                {writeOffs.map((r, wi) => (
                                  <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }
            })}
          </tbody>
        </Table>
      )}

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Transaction Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          Use the filter panel to refine the dataset by date range, practice, and status.
          Totals reflect all filtered rows (not just this page). CSV export downloads the entire filtered dataset.
          Credit notes are shown as negative amounts in brackets. <strong>*Excluding</strong> and <strong>*VAT</strong> amounts are only populated for practices registered for VAT.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>Got it</Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Date range */}
          <div className="mb-3">
            <h6 className="text-primary fw-normal ms-3 mb-2">Date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices(allPractices)}>
                    Select all
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices([])}>
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() => setTmpPractices((arr) => toggleIn(arr, name))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpStatuses(statusOptions)}>
                    Select all
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpStatuses([])}>
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column">
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
